/**
 * Audio utilities
 *
 * Provides audio-related helper functions including test tone generation.
 */

/* global btoa */

/**
 * Generate a 2-second 440Hz sine wave as WAV data URL
 * Used as a fallback for speaker test when WebAudio fails
 *
 * @param durationSec - Duration of the tone in seconds (default: 2.0)
 * @param frequency - Frequency in Hz (default: 440)
 * @param volume - Volume level 0-1 (default: 0.18)
 * @returns Data URL containing the WAV audio
 */
export function generateTestToneWav(
  durationSec: number = 2.0,
  frequency: number = 440,
  volume: number = 0.18,
): string {
  const sampleRate = 48000;
  const frames = Math.floor(durationSec * sampleRate);
  const buffer = new ArrayBuffer(44 + frames * 2);
  const view = new DataView(buffer);

  // Helper to write string to buffer
  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  // RIFF header
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + frames * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true); // PCM header length
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, frames * 2, true);

  // Generate samples
  let offset = 44;
  for (let i = 0; i < frames; i++) {
    const t = i / sampleRate;
    const sample = Math.sin(2 * Math.PI * frequency * t) * volume;
    const s = Math.max(-1, Math.min(1, sample));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    offset += 2;
  }

  // Convert to base64
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return `data:audio/wav;base64,${btoa(binary)}`;
}

/**
 * Check if the browser supports setSinkId for audio output selection
 */
export function supportsSinkId(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId ===
    'function'
  );
}
